/**
 * /market/reserva/:token — Gestionar reserva (ver, modificar, cancelar) + calendario.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/market/reserva/$token")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  component: ManageReservationPage,
  head: () => ({
    meta: [
      { title: "Gestionar reserva — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type ReservationView = {
  id: string;
  status: string;
  reservedDate: string;
  reservedTime: string;
  partySize: number;
  guestName: string;
  guestPhone: string | null;
  guestEmail: string | null;
  notes: string | null;
  serviceId: string;
  serviceNombre?: string;
  tableNombre?: string;
  canModify: boolean;
  canCancel: boolean;
};

function ManageReservationPage() {
  const { token } = Route.useParams();
  const [loading, setLoading] = useState(true);
  const { confirm } = useAppDialogs();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reservation, setReservation] = useState<ReservationView | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [googleCalendarUrl, setGoogleCalendarUrl] = useState<string | null>(null);
  const [mode, setMode] = useState<"view" | "edit" | "done">("view");
  const [doneMsg, setDoneMsg] = useState("");

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/reservations/manage?token=${encodeURIComponent(token)}`, {
        credentials: "include",
      });
      const data = (await res.json()) as {
        error?: string;
        reservation?: ReservationView;
        tenant?: { name: string; slug: string };
        googleCalendarUrl?: string;
      };
      if (!res.ok || !data.reservation) {
        setError(data.error ?? "Reserva no encontrada");
        setReservation(null);
        return;
      }
      setReservation(data.reservation);
      setTenantName(data.tenant?.name ?? "");
      setTenantSlug(data.tenant?.slug ?? "");
      setGoogleCalendarUrl(data.googleCalendarUrl ?? null);
      setDate(data.reservation.reservedDate);
      setTime(data.reservation.reservedTime.slice(0, 5));
      setPartySize(String(data.reservation.partySize));
      setNotes(data.reservation.notes ?? "");
    } catch {
      setError("No se pudo cargar la reserva");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = async (body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/reservations/manage?token=${encodeURIComponent(token)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as { error?: string; reservation?: ReservationView };
      if (!res.ok) {
        toast.error(data.error ?? "No se pudo actualizar");
        return false;
      }
      return true;
    } catch {
      toast.error("Error de red");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirm({ title: "¿Confirmás que querés cancelar esta reserva?", destructive: true, confirmLabel: "Cancelar reserva" }))) return;
    const ok = await patch({ action: "cancel" });
    if (ok) {
      setDoneMsg("Tu reserva fue cancelada.");
      setMode("done");
      toast.success("Reserva cancelada");
    }
  };

  const handleModify = async () => {
    const ok = await patch({
      action: "modify",
      date,
      time,
      partySize: Number(partySize) || 1,
      notes: notes.trim() || null,
    });
    if (ok) {
      toast.success("Reserva modificada. Si dejaste email, te enviamos la actualización.");
      setMode("view");
      await load();
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[#0a1620] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-[#0a1620] p-6 text-center text-white">
        <CalendarCheck className="h-8 w-8 text-white/40" />
        <h1 className="text-xl font-semibold">No encontramos la reserva</h1>
        <p className="max-w-sm text-sm text-white/60">{error ?? "El enlace puede haber expirado."}</p>
        {tenantSlug ? (
          <Button asChild variant="secondary" className="mt-2">
            <Link to="/market/$slug/reservar" params={{ slug: tenantSlug }}>
              Hacer una nueva reserva
            </Link>
          </Button>
        ) : null}
      </div>
    );
  }

  if (mode === "done") {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-[#0a1620] p-6 text-center text-white">
        <CalendarCheck className="h-10 w-10 text-emerald-400" />
        <h1 className="text-xl font-semibold">Listo</h1>
        <p className="max-w-md text-sm text-white/70">{doneMsg}</p>
        {tenantSlug ? (
          <Button asChild className="mt-2">
            <Link to="/market/$slug" params={{ slug: tenantSlug }}>
              Ir al menú de {tenantName || "la tienda"}
            </Link>
          </Button>
        ) : null}
      </div>
    );
  }

  const timeLabel = reservation.reservedTime.slice(0, 5);
  const statusLabel =
    reservation.status === "confirmed"
      ? "Confirmada"
      : reservation.status === "seated"
        ? "Sentada / en curso"
        : reservation.status === "cancelled"
          ? "Cancelada"
          : reservation.status === "no_show"
            ? "No-show"
            : reservation.status;

  return (
    <div className="min-h-dvh bg-[#0a1620] px-4 py-10 text-white">
      <div className="mx-auto w-full max-w-md space-y-5">
        <div className="space-y-1 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-white/50">{tenantName}</p>
          <h1 className="text-2xl font-semibold tracking-tight">Tu reserva</h1>
          <p className="text-sm text-white/60">{statusLabel}</p>
        </div>

        {mode === "view" ? (
          <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-white/50">Nombre</dt>
                <dd className="font-medium">{reservation.guestName}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-white/50">Fecha</dt>
                <dd className="font-medium tabular-nums">{reservation.reservedDate}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-white/50">Hora</dt>
                <dd className="font-medium tabular-nums">{timeLabel} hs</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-white/50">Personas</dt>
                <dd className="font-medium">{reservation.partySize}</dd>
              </div>
              {reservation.serviceNombre ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-white/50">Turno</dt>
                  <dd className="font-medium">{reservation.serviceNombre}</dd>
                </div>
              ) : null}
              {reservation.tableNombre ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-white/50">Mesa</dt>
                  <dd className="font-medium">{reservation.tableNombre}</dd>
                </div>
              ) : null}
            </dl>

            <div className="grid gap-2 pt-2">
              {googleCalendarUrl ? (
                <Button asChild className="h-11 w-full">
                  <a href={googleCalendarUrl} target="_blank" rel="noopener noreferrer">
                    Agregar a Google Calendar
                  </a>
                </Button>
              ) : null}
              <Button asChild variant="secondary" className="h-11 w-full">
                <a href={`/ics/reservation/${encodeURIComponent(token)}`}>Descargar .ics</a>
              </Button>
              {reservation.canModify ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="h-11 w-full"
                  onClick={() => setMode("edit")}
                >
                  Modificar fecha / hora
                </Button>
              ) : null}
              {reservation.canCancel ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 w-full text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  disabled={busy}
                  onClick={() => void handleCancel()}
                >
                  Cancelar reserva
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-white/70">Elegí el nuevo horario. Validamos cupo al guardar.</p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-white/50">Fecha</label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border-white/15 bg-[#0a1620] text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Hora (HH:MM)</label>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="border-white/15 bg-[#0a1620] text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Personas</label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  className="border-white/15 bg-[#0a1620] text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Notas (opcional)</label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="border-white/15 bg-[#0a1620] text-white"
                  placeholder="Alergias, ocasión…"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Button type="button" className="h-11" disabled={busy} onClick={() => void handleModify()}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar cambios"}
              </Button>
              <Button type="button" variant="ghost" className="h-11" onClick={() => setMode("view")}>
                Volver
              </Button>
            </div>
          </div>
        )}

        {reservation.guestPhone ? (
          <a
            href={`https://wa.me/${reservation.guestPhone.replace(/\D/g, "")}`}
            className="flex items-center justify-center gap-2 text-xs text-white/45 hover:text-white/70"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Contacto del local vía el teléfono de la reserva
          </a>
        ) : null}
      </div>
    </div>
  );
}
